"use client"

import { Package, ShoppingBag, Edit, Trash2, Sparkles, Coins, Copy } from "lucide-react"

interface ShopItemData {
    sitem_id: number
    nome: string
    amount: number
    enchant: number
}

interface ShopPackCardProps {
    pack: {
        spack_id: number
        nome: string
        imagem: string | null
        valor: number
        items: ShopItemData[]
    }
    variant: "user" | "admin"
    categoryName?: string
    creditsPerUsd?: number
    onBuy?: () => void
    onEdit?: () => void
    onDelete?: () => void
    onDuplicate?: () => void
}

export function ShopPackCard({ pack, variant, categoryName, creditsPerUsd, onBuy, onEdit, onDelete, onDuplicate }: ShopPackCardProps) {
    const coinItem = pack.items.find(i => i.nome === "Dragon Coin")
    const usdPrice = creditsPerUsd ? (pack.valor / creditsPerUsd).toFixed(2) : null

    return (
        <div className="flex flex-col rounded-2xl border border-slate-800/60 bg-slate-900/50 overflow-hidden hover:border-slate-700 transition-all group">
            {/* Image */}
            <div className="h-36 bg-slate-950 relative flex items-center justify-center border-b border-slate-800/50">
                {pack.imagem ? (
                    <img
                        src={pack.imagem}
                        alt={pack.nome}
                        className="w-full h-full object-cover opacity-60 group-hover:opacity-90 transition-opacity"
                    />
                ) : (
                    <Package className="w-12 h-12 text-slate-800 group-hover:text-slate-600 transition-colors" />
                )}

                {/* Credits badge */}
                <div className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 bg-slate-950/90 border border-purple-500/30 rounded-xl shadow-2xl backdrop-blur-sm">
                    <Coins className="w-3.5 h-3.5 text-purple-400" />
                    <span className="text-purple-300 font-black text-sm tracking-tight">
                        {pack.valor.toLocaleString()}
                    </span>
                    <span className="text-purple-500 text-[9px] font-black uppercase tracking-widest">Credits</span>
                </div>

                {/* USD price badge */}
                {usdPrice && (
                    <div className="absolute top-3 left-3 flex items-center gap-1 px-2.5 py-1.5 bg-slate-950/90 border border-green-500/30 rounded-xl shadow-2xl backdrop-blur-sm">
                        <span className="text-green-400 font-black text-sm">${usdPrice}</span>
                        <span className="text-green-600 text-[9px] font-black uppercase tracking-widest">USD</span>
                    </div>
                )}

                {/* Dragon Coin highlight */}
                {coinItem && (
                    <div className="absolute bottom-3 left-3 flex items-center gap-1.5 px-2.5 py-1 bg-amber-500/10 border border-amber-500/30 rounded-lg backdrop-blur-sm">
                        <Sparkles className="w-3 h-3 text-amber-400" />
                        <span className="text-amber-300 font-black text-xs">
                            {coinItem.amount.toLocaleString()} Coins
                        </span>
                    </div>
                )}
            </div>

            {/* Info */}
            <div className="p-5 flex flex-col flex-grow">
                {categoryName && (
                    <div className="text-[9px] font-black text-blue-500 uppercase tracking-widest mb-1">{categoryName}</div>
                )}
                <h4 className="text-white font-bold text-base mb-3 group-hover:text-purple-300 transition-colors line-clamp-1">
                    {pack.nome}
                </h4>

                {/* Items list */}
                {pack.items.length > 0 && (
                    <div className="border-t border-slate-800/50 pt-3 mb-4 flex-grow space-y-1.5">
                        <div className="text-[9px] font-black text-slate-600 uppercase tracking-widest mb-2">Contents</div>
                        {pack.items.map((item) => (
                            <div
                                key={item.sitem_id}
                                className="flex items-center justify-between text-sm"
                            >
                                <span className="text-slate-300 text-xs flex items-center gap-1.5">
                                    <span>{item.nome}</span>
                                    {item.enchant > 0 && (
                                        <span className="text-purple-300 font-extrabold text-[10px] bg-purple-500/20 border border-purple-500/40 px-1.5 py-0.2 rounded leading-tight shadow-sm">
                                            +{item.enchant}
                                        </span>
                                    )}
                                </span>
                                <span className="text-slate-500 text-[10px] font-bold tabular-nums">
                                    ×{item.amount.toLocaleString()}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Actions */}
                {variant === "user" && (
                    <button
                        onClick={onBuy}
                        className="w-full py-3 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-bold rounded-xl transition-all shadow-lg shadow-purple-900/20 active:scale-[0.97] flex items-center justify-center gap-2 text-sm"
                    >
                        <ShoppingBag className="w-4 h-4" />
                        Buy Now
                    </button>
                )}

                {variant === "admin" && (
                    <div className="flex items-center justify-between pt-2 border-t border-slate-800/50">
                        <span className="text-[10px] text-slate-500 font-bold uppercase tracking-tight">
                            {pack.items.length} Item{pack.items.length !== 1 ? "s" : ""}
                        </span>
                        <div className="flex gap-2">
                            <button
                                onClick={onDuplicate}
                                title="Duplicate"
                                className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-blue-400 transition-colors"
                            >
                                <Copy className="w-4 h-4" />
                            </button>
                            <button
                                onClick={onEdit}
                                className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
                            >
                                <Edit className="w-4 h-4" />
                            </button>
                            <button
                                onClick={onDelete}
                                className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-red-500 transition-colors"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
